import { mapInfo, RADAR_SIZE, type Level, type MapId } from "./catalog.ts";
import { levelAt, worldToRadar } from "./coords.ts";

/** The parts of a map the app sorts things into. */
export type ZoneArea = "a" | "b" | "mid" | "other";
export type ZoneSite = "a" | "b";

/** A box on the radar, in fractions (left, top, right, bottom). */
export interface RadarBox {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
}

interface MapZones {
  /** Where the bomb can be planted: the orange outlines on Valve's radars. */
  sites: Partial<Record<ZoneSite, RadarBox & { level: Level }>>;
  /**
   * Points spread over each part of the map (site, its entrances, mid). A spot
   * belongs to the part of the nearest point, if one is close enough.
   */
  anchors: { area: Exclude<ZoneArea, "other">; u: number; v: number; level?: Level }[];
}

const box = (u0: number, v0: number, u1: number, v1: number, level: Level = "upper") => ({ u0, v0, u1, v1, level });
const pts = (area: Exclude<ZoneArea, "other">, list: [number, number][], level?: Level) =>
  list.map(([u, v]) => ({ area, u, v, ...(level ? { level } : {}) }));

/**
 * Drawn over Valve's radars (packages/maps/radars) and checked by eye; the
 * site letters come from Valve's overview files (bombA_x/bombB_x), Anubis from
 * its callouts. Coarse on purpose: forms let players change the area.
 */
export const ZONES: Record<MapId, MapZones> = {
  de_mirage: {
    sites: { a: box(0.505, 0.73, 0.575, 0.795), b: box(0.195, 0.235, 0.265, 0.32) },
    anchors: [
      ...pts("a", [[0.54, 0.76], [0.66, 0.66], [0.75, 0.76], [0.43, 0.66], [0.44, 0.61], [0.4, 0.62], [0.47, 0.71], [0.31, 0.7], [0.61, 0.71], [0.77, 0.6]]),
      ...pts("b", [[0.23, 0.28], [0.3, 0.18], [0.4, 0.18], [0.48, 0.17], [0.56, 0.2], [0.19, 0.42], [0.31, 0.38], [0.16, 0.31], [0.25, 0.44]]),
      ...pts("mid", [[0.5, 0.45], [0.6, 0.44], [0.7, 0.47], [0.39, 0.48], [0.36, 0.52], [0.45, 0.33], [0.5, 0.52]]),
    ],
  },
  de_dust2: {
    sites: { a: box(0.761, 0.134, 0.828, 0.203), b: box(0.165, 0.08, 0.25, 0.171) },
    anchors: [
      ...pts("a", [[0.8, 0.16], [0.7, 0.1], [0.88, 0.28], [0.88, 0.45], [0.88, 0.62], [0.62, 0.35], [0.68, 0.25], [0.61, 0.2]]),
      ...pts("b", [[0.21, 0.12], [0.1, 0.25], [0.1, 0.4], [0.15, 0.55], [0.22, 0.45], [0.32, 0.45], [0.31, 0.2]]),
      ...pts("mid", [[0.5, 0.45], [0.5, 0.6], [0.48, 0.74], [0.47, 0.3], [0.4, 0.38]]),
    ],
  },
  de_inferno: {
    sites: { a: box(0.775, 0.625, 0.845, 0.735), b: box(0.44, 0.17, 0.53, 0.26) },
    anchors: [
      ...pts("a", [[0.81, 0.69], [0.72, 0.61], [0.88, 0.8], [0.72, 0.79], [0.72, 0.48], [0.7, 0.4], [0.6, 0.68]]),
      ...pts("b", [[0.48, 0.22], [0.58, 0.15], [0.45, 0.4], [0.44, 0.52], [0.4, 0.6], [0.62, 0.27], [0.68, 0.17]]),
      ...pts("mid", [[0.52, 0.66], [0.42, 0.73], [0.32, 0.74], [0.5, 0.85], [0.6, 0.86]]),
    ],
  },
  de_ancient: {
    sites: { a: box(0.265, 0.235, 0.34, 0.285), b: box(0.7, 0.38, 0.79, 0.44) },
    anchors: [
      ...pts("a", [[0.3, 0.26], [0.2, 0.25], [0.18, 0.45], [0.22, 0.6], [0.33, 0.17], [0.31, 0.45], [0.3, 0.7]]),
      ...pts("b", [[0.75, 0.41], [0.66, 0.3], [0.8, 0.28], [0.64, 0.43], [0.78, 0.55], [0.72, 0.72], [0.62, 0.55]]),
      ...pts("mid", [[0.47, 0.35], [0.47, 0.48], [0.49, 0.62], [0.42, 0.7], [0.54, 0.29]]),
    ],
  },
  de_anubis: {
    sites: { a: box(0.7, 0.19, 0.81, 0.31), b: box(0.29, 0.455, 0.363, 0.527) },
    anchors: [
      ...pts("a", [[0.75, 0.25], [0.64, 0.2], [0.62, 0.33], [0.8, 0.4], [0.72, 0.5], [0.63, 0.62], [0.62, 0.75]]),
      ...pts("b", [[0.32, 0.49], [0.2, 0.57], [0.3, 0.68], [0.33, 0.78], [0.3, 0.33], [0.38, 0.4]]),
      ...pts("mid", [[0.5, 0.4], [0.5, 0.52], [0.47, 0.65], [0.44, 0.3]]),
    ],
  },
  de_cache: {
    sites: { a: box(0.283, 0.214, 0.349, 0.318), b: box(0.296, 0.762, 0.395, 0.854) },
    anchors: [
      ...pts("a", [[0.32, 0.26], [0.32, 0.37], [0.42, 0.23], [0.47, 0.33], [0.55, 0.22], [0.2, 0.36]]),
      ...pts("b", [[0.345, 0.8], [0.24, 0.7], [0.42, 0.77], [0.45, 0.67], [0.2, 0.58], [0.55, 0.75]]),
      ...pts("mid", [[0.5, 0.52], [0.6, 0.52], [0.38, 0.55], [0.7, 0.58], [0.3, 0.47]]),
    ],
  },
  de_nuke: {
    // A is upstairs, B is on the lower radar right under it.
    sites: { a: box(0.55, 0.474, 0.603, 0.536), b: box(0.535, 0.505, 0.605, 0.58, "lower") },
    anchors: [
      ...pts("a", [[0.58, 0.5], [0.66, 0.45], [0.7, 0.5], [0.5, 0.45], [0.57, 0.38]], "upper"),
      ...pts("mid", [[0.72, 0.62], [0.65, 0.7], [0.78, 0.7]], "upper"),
      ...pts("b", [[0.57, 0.54], [0.66, 0.58], [0.62, 0.66], [0.5, 0.66], [0.68, 0.45], [0.58, 0.42]], "lower"),
    ],
  },
};

/** How far from the nearest anchor a spot still belongs to its part of the map, in world units (about 17 m). */
const AREA_REACH = 650;
/** Room around a site's outline where a plant still counts, in world units: the outlines are drawn tight. */
const SITE_MARGIN = 80;

/** The part of the map a world spot is in: a site, mid, or other (spawns, far corners). */
export function areaAt(map: MapId, x: number, y: number, z: number | null = null): ZoneArea {
  const p = worldToRadar(map, x, y);
  const level = levelAt(map, z);
  const reach = AREA_REACH / mapInfo(map).scale / RADAR_SIZE;
  let best: ZoneArea = "other";
  let bestD = reach;
  for (const a of ZONES[map].anchors) {
    if ((a.level ?? level) !== level) continue;
    const d = Math.hypot(p.u - a.u, p.v - a.v);
    if (d < bestD) {
      bestD = d;
      best = a.area;
    }
  }
  return best;
}

/** A site's plant zone on the radar. */
export const siteBox = (map: MapId, site: ZoneSite) => ZONES[map].sites[site] ?? null;

/** The site whose plant zone holds this spot, or null outside both. */
export function siteAt(map: MapId, x: number, y: number, z: number | null = null): ZoneSite | null {
  const p = worldToRadar(map, x, y);
  const level = levelAt(map, z);
  const m = SITE_MARGIN / mapInfo(map).scale / RADAR_SIZE;
  for (const site of ["a", "b"] as const) {
    const b = ZONES[map].sites[site];
    if (b && b.level === level && p.u >= b.u0 - m && p.u <= b.u1 + m && p.v >= b.v0 - m && p.v <= b.v1 + m) return site;
  }
  return null;
}

/** Where a part of the map sits on the radar: the site's centre, or mid's first anchor. */
export function areaCentre(map: MapId, area: ZoneArea): { u: number; v: number; level: Level } | null {
  if (area === "a" || area === "b") {
    const b = ZONES[map].sites[area];
    if (b) return { u: (b.u0 + b.u1) / 2, v: (b.v0 + b.v1) / 2, level: b.level };
  }
  const first = ZONES[map].anchors.find((a) => a.area === area);
  return first ? { u: first.u, v: first.v, level: first.level ?? "upper" } : null;
}
